export const TERMINAL_MAX_TABS = 5;

export interface TerminalTab {
	terminalId: string;
}

export interface TerminalTabsState {
	tabs: TerminalTab[];
	activeTerminalId: string;
}

type TerminalIdGenerator = () => string;

function defaultGenerateId(): string {
	return crypto.randomUUID();
}

export function createTerminalTab(
	generateId: TerminalIdGenerator = defaultGenerateId,
): TerminalTab {
	return { terminalId: generateId() };
}

export function createInitialTerminalTabs(
	generateId: TerminalIdGenerator = defaultGenerateId,
): TerminalTabsState {
	const tab = createTerminalTab(generateId);
	return { tabs: [tab], activeTerminalId: tab.terminalId };
}

function isValidStoredState(value: unknown): value is TerminalTabsState {
	if (typeof value !== "object" || value === null) return false;
	const state = value as Partial<TerminalTabsState>;
	if (!Array.isArray(state.tabs) || typeof state.activeTerminalId !== "string") return false;
	if (state.tabs.length === 0 || state.tabs.length > TERMINAL_MAX_TABS) return false;

	const terminalIds = new Set<string>();
	for (const tab of state.tabs) {
		const terminalId =
			typeof tab === "object" && tab !== null
				? (tab as Partial<TerminalTab>).terminalId
				: undefined;
		if (typeof terminalId !== "string" || terminalId.length === 0) {
			return false;
		}
		if (terminalIds.has(terminalId)) return false;
		terminalIds.add(terminalId);
	}
	return terminalIds.has(state.activeTerminalId);
}

/** 保存済み状態を検証し、無効なら新しい単一タブへ戻す。 */
export function restoreTerminalTabsState(
	stored: unknown,
	generateId: TerminalIdGenerator = defaultGenerateId,
): TerminalTabsState {
	if (!isValidStoredState(stored)) return createInitialTerminalTabs(generateId);
	return {
		tabs: stored.tabs.map((tab) => ({ terminalId: tab.terminalId })),
		activeTerminalId: stored.activeTerminalId,
	};
}

export function canAddTerminalTab(state: TerminalTabsState): boolean {
	return state.tabs.length < TERMINAL_MAX_TABS;
}

export function addTerminalTab(
	state: TerminalTabsState,
	generateId: TerminalIdGenerator = defaultGenerateId,
): TerminalTabsState {
	if (!canAddTerminalTab(state)) return state;
	const tab = createTerminalTab(generateId);
	return { tabs: [...state.tabs, tab], activeTerminalId: tab.terminalId };
}

export function activateTerminalTab(
	state: TerminalTabsState,
	terminalId: string,
): TerminalTabsState {
	if (
		state.activeTerminalId === terminalId ||
		!state.tabs.some((tab) => tab.terminalId === terminalId)
	) {
		return state;
	}
	return { ...state, activeTerminalId: terminalId };
}

/**
 * タブを閉じる。アクティブタブなら同じ位置、末尾なら左隣を選ぶ。最後の一枚は新しいタブに置換する。
 */
export function closeTerminalTab(
	state: TerminalTabsState,
	terminalId: string,
	createReplacement: () => TerminalTab,
): TerminalTabsState {
	const index = state.tabs.findIndex((tab) => tab.terminalId === terminalId);
	if (index === -1) return state;
	if (state.tabs.length === 1) {
		const replacement = createReplacement();
		return { tabs: [replacement], activeTerminalId: replacement.terminalId };
	}

	const tabs = [...state.tabs.slice(0, index), ...state.tabs.slice(index + 1)];
	if (state.activeTerminalId !== terminalId) return { ...state, tabs };
	return { tabs, activeTerminalId: tabs[Math.min(index, tabs.length - 1)].terminalId };
}
